import moment from 'moment-timezone';
import { OfferOneOffSlotDTO, OfferRedeemSlotsForm, OfferSlotDay } from './type';

export const MAX_WEEKLY_GROUPS = 20;
export const MAX_ONE_OFF_SLOTS = 50;
export const WALL_CLOCK_FORMAT = 'YYYY-MM-DDTHH:mm';
const HOURS_PATTERN = /^([01]\d|2[0-3]):[0-5]\d$/;
const END_OF_DAY = '24:00';

export type OfferSlotErrorKey =
  | 'emptyDays'
  | 'emptyHours'
  | 'invalidRange'
  | 'overlap'
  | 'duplicateDay'
  | 'oneOffIncomplete'
  | 'oneOffOrder'
  | 'oneOffPast'
  | 'tooManyGroups'
  | 'tooManyOneOff';

export type OfferSlotError = {
  key: OfferSlotErrorKey;
  params: Record<string, string | number>;
  scope: 'weekly' | 'oneOff' | 'form';
  // Zero-based position of the faulty group or one-off row, to show the error next to it.
  index?: number;
};

export const endForApi = (end: string): string => (end === '00:00' ? END_OF_DAY : end);

export const wallClockToInstant = (wallClock: string, timezone: string) =>
  moment.tz(wallClock, WALL_CLOCK_FORMAT, timezone);

const toMinutes = (value: string): number => {
  const [hours, minutes] = value.split(':').map(Number);
  return hours * 60 + minutes;
};

type DayRange = {
  group: number;
  range: number;
  start: number;
  end: number;
};

const overlapErrors = (form: OfferRedeemSlotsForm): OfferSlotError[] => {
  const byDay = new Map<OfferSlotDay, DayRange[]>();
  form.weekly.forEach((group, groupIndex) => {
    group.hours.forEach((hours, rangeIndex) => {
      if (!HOURS_PATTERN.test(hours.start) || !HOURS_PATTERN.test(hours.end)) return;
      const range = {
        group: groupIndex,
        range: rangeIndex,
        start: toMinutes(hours.start),
        end: toMinutes(endForApi(hours.end)),
      };
      if (range.start >= range.end) return;
      for (const day of Array.from(new Set(group.days))) byDay.set(day, [...(byDay.get(day) ?? []), range]);
    });
  });

  const errors: OfferSlotError[] = [];
  byDay.forEach((ranges, day) => {
    ranges.forEach((first, i) => {
      for (const second of ranges.slice(i + 1)) {
        // Touching ranges (17:00 end, 17:00 start) are allowed.
        if (first.start < second.end && second.start < first.end) {
          errors.push({
            key: 'overlap',
            scope: 'weekly',
            index: second.group,
            params: {
              day,
              first: `${first.group + 1}.${first.range + 1}`,
              second: `${second.group + 1}.${second.range + 1}`,
            },
          });
        }
      }
    });
  });
  return errors;
};

const weeklyErrors = (form: OfferRedeemSlotsForm): OfferSlotError[] => {
  const errors: OfferSlotError[] = [];
  form.weekly.forEach((group, index) => {
    const position = index + 1;
    if (group.days.length === 0) errors.push({ key: 'emptyDays', scope: 'weekly', index, params: { group: position } });
    if (group.hours.length === 0)
      errors.push({ key: 'emptyHours', scope: 'weekly', index, params: { group: position } });
    for (const day of group.days.filter((day, i) => group.days.indexOf(day) !== i)) {
      errors.push({ key: 'duplicateDay', scope: 'weekly', index, params: { group: position, day } });
    }
    group.hours.forEach((hours, rangeIndex) => {
      const isValid =
        HOURS_PATTERN.test(hours.start) &&
        (HOURS_PATTERN.test(hours.end) || hours.end === END_OF_DAY) &&
        toMinutes(hours.start) < toMinutes(endForApi(hours.end));
      if (!isValid) {
        errors.push({
          key: 'invalidRange',
          scope: 'weekly',
          index,
          params: { group: position, range: rangeIndex + 1 },
        });
      }
    });
  });
  return errors;
};

const isStoredSlot = (stored: OfferOneOffSlotDTO[], start: moment.Moment, end: moment.Moment) =>
  stored.some(slot => moment(slot.start).isSame(start) && moment(slot.end).isSame(end));

const oneOffErrors = (
  form: OfferRedeemSlotsForm,
  timezone: string,
  stored: OfferOneOffSlotDTO[],
  now: moment.Moment,
): OfferSlotError[] => {
  const errors: OfferSlotError[] = [];
  form.oneOff.forEach((slot, index) => {
    const params = { slot: index + 1 };
    if (!slot.start || !slot.end) {
      errors.push({ key: 'oneOffIncomplete', scope: 'oneOff', index, params });
      return;
    }
    const start = wallClockToInstant(slot.start, timezone);
    const end = wallClockToInstant(slot.end, timezone);
    if (!start.isValid() || !end.isValid() || !end.isAfter(start)) {
      errors.push({ key: 'oneOffOrder', scope: 'oneOff', index, params });
    } else if (!end.isAfter(now) && !isStoredSlot(stored, start, end)) {
      // An unchanged ended slot stays accepted so an offer whose slots ended can still be edited.
      errors.push({ key: 'oneOffPast', scope: 'oneOff', index, params });
    }
  });
  return errors;
};

export const validateRedeemSlots = (
  form: OfferRedeemSlotsForm | null | undefined,
  timezone: string,
  stored: OfferOneOffSlotDTO[] = [],
  now: moment.Moment = moment(),
): OfferSlotError[] => {
  if (!form) return [];
  const errors: OfferSlotError[] = [];
  if (form.weekly.length > MAX_WEEKLY_GROUPS) {
    errors.push({ key: 'tooManyGroups', scope: 'form', params: { max: MAX_WEEKLY_GROUPS } });
  }
  if (form.oneOff.length > MAX_ONE_OFF_SLOTS) {
    errors.push({ key: 'tooManyOneOff', scope: 'form', params: { max: MAX_ONE_OFF_SLOTS } });
  }
  return [...errors, ...weeklyErrors(form), ...overlapErrors(form), ...oneOffErrors(form, timezone, stored, now)];
};
